import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import { Button } from "../../../components/src/Button";
import { flexRender, SortingState, useTable } from "@tanstack/react-table";
import { createReactTableColumnHelper, reactTableFeatures } from "../../../components/src/react-table/features";

interface ExcelData {
    [key: string]: any;
}

export const LimitTrialPage = (): React.ReactElement => {
    const [data, setData] = useState<ExcelData[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [fileName, setFileName] = useState<string>("");

    const columnHelper = createReactTableColumnHelper<ExcelData>();

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];
            if (!file) return;

            setFileName(file.name);

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: "array" });

                    // Get the first worksheet
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    // Convert to JSON
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    if (jsonData.length === 0) {
                        alert("The Excel file appears to be empty.");
                        return;
                    }

                    // First row as headers
                    const headers = jsonData[0] as string[];
                    const rows = jsonData.slice(1) as any[][];

                    // Convert to object array
                    const objectData = rows.map((row) => {
                        const obj: ExcelData = {};
                        headers.forEach((header, index) => {
                            obj[header] = row[index] || "";
                        });
                        return obj;
                    });

                    setData(objectData);

                    // Create columns dynamically
                    const dynamicColumns = headers.map((header) =>
                        columnHelper.accessor(header, {
                            header: header,
                            cell: (info) => info.getValue()
                        })
                    );

                    setColumns(dynamicColumns);
                } catch (error) {
                    console.error("Error reading Excel file:", error);
                    alert("Error reading Excel file. Please make sure it's a valid Excel file.");
                }
            };

            reader.readAsArrayBuffer(file);
        },
        [columnHelper]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.ms-excel": [".xls"]
        },
        multiple: false
    });

    const table = useTable({
        features: reactTableFeatures,
        data,
        columns,
        state: {
            sorting
        },
        onSortingChange: setSorting,
        initialState: {
            pagination: {
                pageIndex: 0,
                pageSize: 20
            }
        }
    });

    const clearData = () => {
        setData([]);
        setColumns([]);
        setFileName("");
    };

    return (
        <div className="w-full" style={{ padding: "20px" }}>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2">
                    <h2>Excel File Upload and Display</h2>

                    {/* Dropzone */}
                    <div
                        {...getRootProps()}
                        style={{
                            border: "2px dashed #ccc",
                            borderRadius: "8px",
                            padding: "40px",
                            textAlign: "center",
                            backgroundColor: isDragActive ? "#f0f8ff" : "#fafafa",
                            cursor: "pointer",
                            marginBottom: "20px",
                            transition: "background-color 0.3s ease"
                        }}
                    >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                            <p>Drop the Excel file here...</p>
                        ) : (
                            <div>
                                <p>Drag & drop an Excel file here, or click to select</p>
                                <p style={{ fontSize: "14px", color: "#666" }}>Supported formats: .xlsx, .xls</p>
                            </div>
                        )}
                    </div>

                    {/* File info and controls */}
                    {fileName && (
                        <div style={{ marginBottom: "20px" }}>
                            <div className="flex justify-between items-center">
                                <div>
                                    <strong>Loaded file:</strong> {fileName}
                                    <span style={{ marginLeft: "10px", color: "#666" }}>({data.length} rows)</span>
                                </div>
                                <Button variant="danger" size="sm" onClick={clearData}>
                                    Clear Data
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    {data.length > 0 && (
                        <div>
                            <div className="table-responsive">
                                <table className="table table-striped table-hover">
                                    <thead>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map((header) => (
                                                    <th
                                                        key={header.id}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                        style={{
                                                            cursor: header.column.getCanSort() ? "pointer" : "default",
                                                            userSelect: "none"
                                                        }}
                                                    >
                                                        <div className="flex items-center">
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getIsSorted() === "asc" && " ↑"}
                                                            {header.column.getIsSorted() === "desc" && " ↓"}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row) => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex justify-between items-center mt-3">
                                <div>
                                    <span>
                                        Showing {table.state.pagination.pageIndex * table.state.pagination.pageSize + 1} to{" "}
                                        {Math.min(
                                            (table.state.pagination.pageIndex + 1) * table.state.pagination.pageSize,
                                            table.getFilteredRowModel().rows.length
                                        )}{" "}
                                        of {table.getFilteredRowModel().rows.length} entries
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => table.setPageIndex(0)}
                                        disabled={!table.getCanPreviousPage()}
                                    >
                                        First
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => table.previousPage()}
                                        disabled={!table.getCanPreviousPage()}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => table.nextPage()}
                                        disabled={!table.getCanNextPage()}
                                    >
                                        Next
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                        disabled={!table.getCanNextPage()}
                                    >
                                        Last
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
